import React, { useState, useEffect } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const CATEGORIES = ['All','Saddles','Bridles','Pads','Boots','Apparel','Blankets','Stable supplies','Other'];
const CONDITIONS = { like_new: 'Like new', good: 'Good', fair: 'Fair', parts: 'Parts' };

const MarketplacePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('browse');
  const [category, setCategory] = useState('All');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Saddles', condition: 'good', price: '', size: '', brand: '', location: '', shipping_available: true, local_pickup: true, allow_offers: true });

  useEffect(() => {
    api.get(`/community/tack${category !== 'All' ? `?category=${category}` : ''}`)
      .then(r => setListings(r.data))
      .catch(() => toast.error('Failed to load listings'))
      .finally(() => setLoading(false));
  }, [category]);

  const handleCreate = async () => {
    if (!form.title || !form.price) return toast.error('Title and price required');
    try {
      const { data } = await api.post('/community/tack', form);
      setListings(p => [data, ...p]);
      setShowCreate(false);
      toast.success('Listing posted!');
    } catch { toast.error('Failed to post listing'); }
  };

  const conditionColor = { like_new: 'badge-green', good: 'badge-blue', fair: 'badge-amber', parts: 'badge-gray' };

  return (
    <div>
      <TopBar title="Used tack" subtitle="Buy & sell in the community"
        rightAction={<button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }} onClick={() => setShowCreate(true)}>+ Sell</button>}
      />

      <div className="tab-bar">
        <div className={`tab-item ${tab === 'browse' ? 'active' : ''}`} onClick={() => setTab('browse')}>Browse</div>
        <div className={`tab-item ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>My listings</div>
      </div>

      <div className="page-content">
        {/* Category filter */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
          {CATEGORIES.map(c => (
            <div key={c} className={`chip ${category === c ? 'selected' : ''}`} onClick={() => setCategory(c)} style={{ whiteSpace: 'nowrap' }}>{c}</div>
          ))}
        </div>

        {loading && <div className="loading-screen"><div className="spinner" /></div>}

        {!loading && listings.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h3>No listings yet</h3>
            <p>Be the first to list used tack in your area</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Post a listing</button>
          </div>
        )}

        {!loading && listings.map(listing => (
          <div key={listing.id} className="card" style={{ marginBottom: 10 }}>
            {listing.images?.length > 0 && (
              <img src={listing.images[0]} alt={listing.title} style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 'var(--radius-md)', marginBottom: 10 }} />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{listing.title}</div>
                {listing.brand && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{listing.brand}{listing.size ? ` · ${listing.size}` : ''}</div>}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cr-green)', marginLeft: 10 }}>${listing.price}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span className={`badge ${conditionColor[listing.condition] || 'badge-gray'}`}>{CONDITIONS[listing.condition]}</span>
              {listing.shipping_available && <span className="badge badge-gray">Ships</span>}
              {listing.local_pickup && <span className="badge badge-gray">Local pickup</span>}
              {listing.allow_offers && <span className="badge badge-amber">Offers welcome</span>}
            </div>
            {listing.seller && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div className="avatar avatar-sm" style={{ background: 'var(--cr-green-light)' }}>
                  {listing.seller.first_name?.[0]}{listing.seller.last_name?.[0]}
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{listing.seller.first_name} {listing.seller.last_name}</span>
                {listing.seller.location && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>📍 {listing.seller.location}</span>}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }}>Contact seller</button>
              {listing.allow_offers && <button className="btn btn-outline btn-sm">Make offer</button>}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2>Post a listing</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" placeholder="e.g. 17.5 Beval close contact saddle" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Category</label><select className="form-input" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>{CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Condition</label><select className="form-input" value={form.condition} onChange={e => setForm(p => ({ ...p, condition: e.target.value }))}>{Object.entries(CONDITIONS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Price ($) *</label><input className="form-input" type="number" placeholder="450" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Size</label><input className="form-input" placeholder='e.g. 17"' value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Brand</label><input className="form-input" placeholder="e.g. CWD, Devoucoux" value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Your location</label><input className="form-input" placeholder="La Vernia, TX" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} placeholder="Condition details, history, reason for selling..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              {[['shipping_available','Ships'],['local_pickup','Local pickup'],['allow_offers','Accept offers']].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.checked }))} style={{ accentColor: 'var(--cr-green)' }} />
                  {label}
                </label>
              ))}
            </div>
            <button className="btn btn-primary btn-full" onClick={handleCreate}>Post listing</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
